import React, { useEffect, useState } from 'react'
import { Modal } from '../../components/Modal/Modal'
import { Col, Container, Modal as BootstrapModal, Row, Toast, ToastContainer } from 'react-bootstrap'
import './MFAModal.scss'
import { Button } from '../../components/Button/Button'
import PhoneInput, { formatPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input'
import VerificationInput from 'react-verification-input'
import { animationData } from '../../assets/animations/icons8-spinner.json'
import Lottie from 'react-lottie'
import { MFAFactor, MFAFactorType, MFAModalPageIdentifier, SMSMFAFactor, TOTPMFAFactor } from '../../models/MFA'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { clearAuthErrors, selectAccountId, selectAuthErrors } from '../../store/auth/authSlice'
import {
  resetFlow,
  selectChallengeStartedForFactorId,
  selectFactorToUpdate,
  selectMFAFactor,
  selectMFAFlow,
  selectPageIdentifier,
  selectRecoveryCodes,
  setChallengeStarted,
  setupMFAForLogin,
  toClaimCodePage,
  toClaimMFAChoicePage,
  toClaimRecoveryCodePage,
  toSetupCompletePage,
  toSetupSMSPage,
  toSetupTOTPCodePage,
  toSetupTOTPPage,
} from '../../store/mfa/mfaSlice'
import {
  useGetConfigQuery,
  useLazyGetFactorQuery,
  useLazyListFactorsQuery,
  useListFactorsQuery,
  useStartFactorChallengeMutation,
  useVerifyFactorMutation,
} from '../../store/mfa/mfa'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { ReactComponent as AuthyIcon } from '../../assets/images/authenticator_authy.svg'
import { ReactComponent as DuoIcon } from '../../assets/images/authenticator_duo.svg'
import { ReactComponent as GoogleIcon } from '../../assets/images/authenticator_google.svg'
import { ReactComponent as MicrosoftIcon } from '../../assets/images/authenticator_microsoft.svg'
import { QRCodeSVG } from 'qrcode.react'
import { useHasAlternativeMethod, useMFAFlow } from './useMFAFlow'

const RESEND_CODE_COUNTDOWN_DURATION = 60
const CODE_LENGTH = 6
const CODE_CHARS = '0-9'

type MFAModalPageBodyProps = {
  login: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface MFAModalPage {
  Header: React.FC
  Body: React.FC<MFAModalPageBodyProps>
  Footer: React.FC
}

const Spinner: React.FC = () => {
  const options = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  }

  return <Lottie options={options} width={40} height={40} isClickToPauseDisabled={true} />
}

const ProgressBody: React.FC = () => (
  <div className='p-5'>
    <Spinner />
  </div>
)

class ProgressPage implements MFAModalPage {
  Header: React.FC = () => <div className='p-5'></div>
  Body: React.FC = () => <ProgressBody />
  Footer: React.FC = () => <div className='p-5'></div>
  onHide = undefined
}

type MFAModalTitleProps = {
  title: string
}

const MFAModalTitle: React.FC<MFAModalTitleProps> = ({ title }) => {
  const flow = useAppSelector(selectMFAFlow)
  return flow === 'login' ? (
    <BootstrapModal.Title className='w-100 text-center pb-3'>
      <div className={'login-modal-header'}>{title}</div>
    </BootstrapModal.Title>
  ) : (
    <h4 className='text-center'>{title}</h4>
  )
}

type VerificationMethodBoxProps = {
  title: string
  description: string
  buttonText?: string
  onClick?: () => void
}

const VerificationMethodBox: React.FC<VerificationMethodBoxProps> = ({ title, description, buttonText = 'Set up', onClick }) => (
  <Row className='verification-method-box mx-0 my-1'>
    <Col xs={9} className='d-flex flex-column gap-2 p-0'>
      <div className='lead-medium'>{title}</div>
      <div>{description}</div>
    </Col>
    <Col className='d-flex flex-column justify-content-center align-items-end p-0'>
      <Button variant='primary' onClick={onClick}>
        {buttonText}
      </Button>
    </Col>
  </Row>
)

type RecoveryCodeBoxProps = {
  onClick?: () => void
}

const RecoveryCodeBox: React.FC<RecoveryCodeBoxProps> = ({ onClick }) => (
  <Row className={'recovery-code-box mx-0 my-1'}>
    <Col xs={9} className={'d-flex flex-column gap-2 p-0'}>
      <div className={'lead-medium'}>Recovery code</div>
      <div>Use backup codes if you are unable to access your account.</div>
    </Col>
    <Col className='d-flex flex-column justify-content-center align-items-end p-0'>
      <Button variant='primaryGhost' onClick={onClick}>
        Select
      </Button>
    </Col>
  </Row>
)

type CodeInputProps = {
  onChange?: (value: string) => void
  onSubmit?: () => void
  errorMessage?: string
}

const CodeInput: React.FC<CodeInputProps> = ({ onChange, onSubmit, errorMessage }) => (
  <>
    <VerificationInput
      autoFocus
      length={CODE_LENGTH}
      validChars={CODE_CHARS}
      placeholder={''}
      classNames={{
        container: 'verification-container',
        character: errorMessage ? 'verification-digit-error' : 'verification-digit',
      }}
      onChange={onChange}
      inputProps={{
        onKeyUp: async (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (onSubmit && e.key === 'Enter') {
            e.preventDefault()
            onSubmit()
          }
        },
      }}
    />
    {errorMessage && <div className={'d-flex justify-content-end paragraph-small zocks-text-danger pt-1'}>{errorMessage}</div>}
  </>
)

class SetupPage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'2-factor authentication is required'} />
  Body: React.FC = () => {
    const dispatch = useAppDispatch()
    const hasTOTPMethod = useHasAlternativeMethod('totp')
    const hasSMSMethod = useHasAlternativeMethod('sms')

    return (
      <Container className='d-flex flex-column gap-2'>
        <Row className={'pb-4'}>
          <Col>
            <div>
              As an extra measure of security 2-factor authentication is now required. From the next log in, Zocks will ask for an authentication code to verify
              your identity.
            </div>
          </Col>
        </Row>
        {hasTOTPMethod && hasSMSMethod && <h6>Choose your preferred verification method</h6>}
        {hasSMSMethod && (
          <VerificationMethodBox
            title={'Text message'}
            description={'Receive verification codes via text message.'}
            onClick={() => dispatch(toSetupSMSPage())}
          />
        )}
        {hasTOTPMethod && (
          <VerificationMethodBox
            title={'Authenticator app'}
            description={'Verify your identity by using a separate authenticator app. We recommend  Google Authenticator.'}
            onClick={() => dispatch(toSetupTOTPPage())}
          />
        )}
      </Container>
    )
  }
  Footer: React.FC = () => <></>
}

type PhoneNumberInputBodyProps = {
  description: React.ReactNode
  ariaLabel: string
  onSubmitPhoneNumber: (phoneNumber: string) => Promise<void>
  validate?: (phoneNumber: string) => string | null
  loading: boolean
  flowErrorMessage: string
  login?: boolean
  showAnotherMethod?: boolean
}

const PhoneNumberInputBody: React.FC<PhoneNumberInputBodyProps> = ({
  description,
  ariaLabel,
  onSubmitPhoneNumber,
  validate,
  loading,
  flowErrorMessage,
  login = false,
  showAnotherMethod = false,
}) => {
  const dispatch = useAppDispatch()
  const [phoneNumber, setPhoneNumber] = React.useState<string>('')
  const [errorMessage, setErrorMessage] = React.useState<string>('')

  const sendPhoneNumber = async () => {
    if (!isPossiblePhoneNumber(phoneNumber)) {
      setErrorMessage('Invalid number')
      return
    }
    const validationError = validate?.(phoneNumber)
    if (validationError) {
      setErrorMessage(validationError)
      return
    }
    await onSubmitPhoneNumber(phoneNumber)
  }

  useEffect(() => {
    setErrorMessage(flowErrorMessage)
  }, [flowErrorMessage])

  return (
    <Container className='d-flex flex-column gap-4'>
      <Row className={'pb-1'}>
        <Col>
          <div>{description}</div>
        </Col>
      </Row>
      <Row>
        <div className='label-small pb-1'>Phone number*</div>
        <PhoneInput
          autoFocus
          international
          defaultCountry='US'
          countryOptionsOrder={['US', 'CA', '...']}
          type='tel'
          numberInputProps={{ className: 'form-control' }}
          aria-label={ariaLabel}
          onChange={(v) => setPhoneNumber(v || '')}
        />
        <FormFeedback>{errorMessage || '\u200B'}</FormFeedback>
      </Row>
      {login ? <Row /> : null}
      <Row className='mx-0'>
        <Button variant={'primary'} className='button-tall w-100' onClick={sendPhoneNumber} loading={loading} disabled={loading}>
          Send code
        </Button>
      </Row>
      {showAnotherMethod ? (
        <Row>
          <div className='button-link-medium' role='button' onClick={() => dispatch(setupMFAForLogin())}>
            Use another method
          </div>
        </Row>
      ) : null}
      <Row />
      <Row>
        <div className='paragraph-medium zocks-text-gray-500'>
          Your phone number will remain private and will not be shared or used for marketing purposes.{' '}
          <a target='_blank' href='https://www.zocks.io/privacy-policy' rel='noreferrer' className='button-link-medium zocks-text-gray-500'>
            Privacy Policy
          </a>
        </div>
      </Row>
    </Container>
  )
}

class SetupSMSPage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'Set up 2-factor authentication'} />
  Body: React.FC<MFAModalPageBodyProps> = ({ login }) => {
    const { createFactorOrValidate, errorMessage, isLoading } = useMFAFlow()
    const hasAlternativeMethod = useHasAlternativeMethod('totp')

    const handleSubmit = async (phoneNumber: string) => {
      await createFactorOrValidate({ type: 'sms', phoneNumber })
    }

    return (
      <PhoneNumberInputBody
        description={
          login
            ? 'If you choose 2-factor authentication via Text Messages (SMS), we only use your phone number for sending authentication codes.'
            : 'To protect your private information, you will need to provide a verification code from your mobile device. It\u2019s easy to setup and only takes a few minutes.'
        }
        ariaLabel='Enter phone number for text message verification code'
        onSubmitPhoneNumber={handleSubmit}
        loading={isLoading}
        flowErrorMessage={errorMessage}
        login={login}
        showAnotherMethod={login && hasAlternativeMethod}
      />
    )
  }
  Footer: React.FC = () => null
}

type CodeBodyProps = {
  onSubmitCode: (code: string) => void
  loading: boolean
  onAnotherMethod?: () => void
  errorMessage?: string
  buttonText?: string
}

const SMSCodeBody: React.FC<CodeBodyProps> = ({ onSubmitCode, onAnotherMethod, loading, errorMessage, buttonText = 'Done' }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const factor = useAppSelector(selectMFAFactor) as SMSMFAFactor | null
  const challengeStartedForFactorId = useAppSelector(selectChallengeStartedForFactorId)
  const [code, setCode] = useState<string>('')
  const [remainingTimeToResendCode, setRemainingTimeToResendCode] = useState<number>(RESEND_CODE_COUNTDOWN_DURATION)
  const [isResendCodeEnabled, setIsResendCodeEnabled] = useState<boolean>(false)

  const [triggerStartFactorChallenge] = useStartFactorChallengeMutation()

  const factorId = factor?.id

  useEffect(() => {
    const startFactorChallenge = async () => {
      if (factorId == null || factorId === challengeStartedForFactorId) {
        return
      }

      dispatch(setChallengeStarted(factorId))
      await triggerStartFactorChallenge({ accountId, factorId })
    }
    startFactorChallenge()
  }, [accountId, factorId, challengeStartedForFactorId, dispatch, triggerStartFactorChallenge])

  useEffect(() => {
    if (remainingTimeToResendCode > 0) {
      const timer = setInterval(() => {
        setRemainingTimeToResendCode((prevTime) => prevTime - 1)
      }, 1000)

      return () => clearInterval(timer)
    } else {
      setIsResendCodeEnabled(true)
    }
  }, [remainingTimeToResendCode])

  const handleResendCode = async () => {
    if (factorId) {
      await triggerStartFactorChallenge({ accountId, factorId })
    }
    setRemainingTimeToResendCode(RESEND_CODE_COUNTDOWN_DURATION)
    setIsResendCodeEnabled(false)
  }

  const getSecondText = (time: number): string => {
    return time === 1 ? 'second' : 'seconds'
  }

  return (
    <Container className='d-flex flex-column gap-4'>
      <Row className={'pb-1'}>
        <Col>
          <div className='code-sent-box p-3'>
            <div className='lead-small'>We've sent a code to</div>
            <div>{formatPhoneNumber(factor?.phoneNumber || '')}</div>
          </div>
        </Col>
      </Row>
      <Row>
        <div className={'label-small pb-1'}>Enter the 6-digit code*</div>
        <CodeInput onChange={(e) => setCode(e)} errorMessage={errorMessage} onSubmit={() => onSubmitCode(code)} />
        <div className='py-2'>
          {!isResendCodeEnabled ? (
            <span className='paragraph-small zocks-text-gray-500'>
              Resend code in {remainingTimeToResendCode} {getSecondText(remainingTimeToResendCode)}
            </span>
          ) : (
            <div className='d-flex gap-1' role='button' onClick={handleResendCode}>
              <i className='bi bi-arrow-clockwise' />
              <div className='button-link-medium'>Resend code</div>
            </div>
          )}
        </div>
      </Row>
      <Row />
      <Row className='mx-0'>
        <Button variant={'primary'} className='button-tall w-100' disabled={code.length !== CODE_LENGTH} onClick={() => onSubmitCode(code)} loading={loading}>
          {buttonText}
        </Button>
      </Row>
      {onAnotherMethod && (
        <Row>
          <div className='button-link-medium' role='button' onClick={onAnotherMethod}>
            Use another method
          </div>
        </Row>
      )}
      <Row />
      <Row>
        <div className='paragraph-medium zocks-text-gray-500'>
          Your phone number will remain private and will not be shared or used for marketing purposes.{' '}
          <a target='_blank' href='https://www.zocks.io/privacy-policy' rel='noreferrer' className='button-link-medium zocks-text-gray-500'>
            Privacy Policy
          </a>
        </div>
      </Row>
    </Container>
  )
}

class SetupSMSCodePage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'Set up 2-factor authentication'} />
  Body: React.FC<MFAModalPageBodyProps> = ({ login }) => {
    const accountId = useAppSelector(selectAccountId)
    const dispatch = useAppDispatch()
    const factor = useAppSelector(selectMFAFactor)! as SMSMFAFactor
    const errors = useAppSelector(selectAuthErrors)
    const hasAlternativeMethod = useHasAlternativeMethod('totp')
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [verifyFactor, { error }] = useVerifyFactorMutation()

    const verifyMFAFactor = async (code: string) => {
      setLoading(true)
      try {
        const { data: response } = await verifyFactor({ accountId, factorId: factor.id, code })
        if (response) {
          dispatch(toSetupCompletePage(response.recoveryCodes || []))
        }
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      if (errors) {
        setErrorMessage(errors?.[0]?.message)
      }
    }, [errors])

    useEffect(() => {
      // @ts-ignore
      setErrorMessage(error?.data?.errors?.[0]?.message || '')
    }, [error])

    return (
      <SMSCodeBody
        onSubmitCode={verifyMFAFactor}
        onAnotherMethod={login && hasAlternativeMethod ? () => dispatch(setupMFAForLogin()) : undefined}
        loading={loading}
        errorMessage={errorMessage}
      />
    )
  }
  Footer: React.FC = () => null
}

const PhoneAppIconAuthy: React.FC = () => (
  <div className='phone-app-icon-authy d-flex align-items-center justify-content-center'>
    <AuthyIcon />
  </div>
)

const PhoneAppIconDuo: React.FC = () => (
  <div className='phone-app-icon-duo d-flex align-items-center justify-content-center'>
    <DuoIcon />
  </div>
)

const PhoneAppIconGoogle: React.FC = () => (
  <div className='phone-app-icon-google d-flex align-items-center justify-content-center'>
    <GoogleIcon />
  </div>
)

const PhoneAppIconMicrosoft: React.FC = () => (
  <div className='phone-app-icon-microsoft d-flex align-items-center justify-content-center'>
    <MicrosoftIcon />
  </div>
)

type PhoneAppRowProps = {
  icon: React.ReactNode
  name: string
  description: string
}

const PhoneAppRow: React.FC<PhoneAppRowProps> = ({ icon, name, description }) => (
  <Row>
    <Col xs='2'>{icon}</Col>
    <Col className='px-4 d-flex flex-column justify-content-center'>
      <h6>{name}</h6>
      <div className='paragraph-medium-extended'>{description}</div>
    </Col>
  </Row>
)

class SetupTOTPPage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title='Authenticator app setup' />
  Body: React.FC<MFAModalPageBodyProps> = ({ login }) => {
    const dispatch = useAppDispatch()
    const { createFactorOrValidate, errorMessage, isLoading } = useMFAFlow()
    const hasAlternativeMethod = useHasAlternativeMethod('sms')

    const createTOTPFactor = async () => {
      await createFactorOrValidate({
        type: 'totp',
      })
    }

    return (
      <Container className='d-flex flex-column gap-4'>
        <Row className={'pb-1'}>
          <Col>
            <div>You'll need to install an authenticator app on your phone to set up 2-factor authentication.</div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Container className='authenticator-apps-box'>
              <Row className='pb-3'>
                <div className='lead-medium text-uppercase text-center'>We recommend one of the following apps</div>
              </Row>
              <Row className='pt-3'>
                <Container className='d-grid gap-3'>
                  <PhoneAppRow icon={<PhoneAppIconAuthy />} name='Authy' description='from Authy inc.' />
                  <PhoneAppRow icon={<PhoneAppIconDuo />} name='Duo Mobile' description='from Duo Security' />
                  <PhoneAppRow icon={<PhoneAppIconGoogle />} name='Google Authenticator' description='from Google inc.' />
                  <PhoneAppRow icon={<PhoneAppIconMicrosoft />} name='Microsoft Authenticator' description='from Microsoft Corporation' />
                </Container>
              </Row>
            </Container>
          </Col>
          <FormFeedback>{errorMessage || '\u200B'}</FormFeedback>
        </Row>
        <Row className='mx-0 pt-2'>
          <div className='paragraph-medium-extended pb-1 px-0'>Got your app open?</div>
          <Button variant={'primary'} className='button-tall w-100' onClick={createTOTPFactor} loading={isLoading}>
            Yes, ready to scan
          </Button>
        </Row>
        {login && hasAlternativeMethod ? (
          <Row>
            <div className='button-link-medium' role='button' onClick={() => dispatch(setupMFAForLogin())}>
              Use another method
            </div>
          </Row>
        ) : null}
      </Container>
    )
  }
  Footer: React.FC = () => null
}

class SetupTOTPScanPage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title='Authenticator app setup' />
  Body: React.FC<MFAModalPageBodyProps> = ({ login }) => {
    const dispatch = useAppDispatch()
    const [showToast, setShowToast] = React.useState(false)
    const factor = useAppSelector(selectMFAFactor) as TOTPMFAFactor | undefined
    const hasAlternativeMethod = useHasAlternativeMethod('sms')
    const copySecretToClipboard = async () => {
      if (factor) {
        await navigator.clipboard.writeText(factor.secret!)
      }
      setShowToast(true)
    }

    const onContinue = () => {
      if (factor) {
        dispatch(toSetupTOTPCodePage(factor))
      }
    }

    return (
      <Container className='d-flex flex-column gap-4'>
        <ToastContainer position='bottom-center' className='p-3 summary-toast'>
          <Toast onClose={() => setShowToast(false)} show={showToast} delay={1500} autohide>
            <Toast.Body>Copied to clipboard!</Toast.Body>
          </Toast>
        </ToastContainer>
        <Row className={'pb-1'}>
          <Col>
            <div>Scan the QR code below with your preferred authenticator app and enter the verification code it creates to Zocks.</div>
          </Col>
        </Row>
        <Row className='mx-0 pb-1'>
          <Container className='qr-code-box'>
            <Row>
              <div className='lead-medium text-uppercase text-center'>Scan QR code</div>
            </Row>
            <Row className='zocks-py-2.0'>
              <QRCodeSVG value={factor?.uri || ''} size={158} bgColor='var(--zocks-gray-100)' title='QR code for authenticator app' />
            </Row>
            <Row>
              <h6 className='text-center pb-1'>Can't scan QR code?</h6>
              <div className='text-center paragraph-medium-extended'>You can also manually enter this text code in the app.</div>
            </Row>
            <Row xs='auto' className='py-3'>
              <Container className='authenticator-app-secret-box d-flex align-items-center justify-content-center p-3'>
                <Row>
                  <Col>{factor?.secret}</Col>
                  <Col className='ps-0'>
                    <div role='button' className='cursor-pointer' onClick={copySecretToClipboard}>
                      <i className='bi bi-copy cursor-pointer' />
                    </div>
                  </Col>
                </Row>
              </Container>
            </Row>
          </Container>
        </Row>
        <Row className='mx-0 pt-4'>
          <Button variant={'primary'} className='button-tall w-100' onClick={onContinue}>
            Continue
          </Button>
        </Row>
        {login && hasAlternativeMethod ? (
          <Row>
            <div className='button-link-medium' role='button' onClick={() => dispatch(setupMFAForLogin())}>
              Use another method
            </div>
          </Row>
        ) : null}
      </Container>
    )
  }
  Footer: React.FC = () => null
}

class SetupTOTPCodePage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title='Authenticator app setup' />
  Body: React.FC<MFAModalPageBodyProps> = ({ login }) => {
    const accountId = useAppSelector(selectAccountId)
    const dispatch = useAppDispatch()
    const factor = useAppSelector(selectMFAFactor) as TOTPMFAFactor | undefined
    const errors = useAppSelector(selectAuthErrors)
    const hasAlternativeMethod = useHasAlternativeMethod('sms')
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [verifyFactor, { error }] = useVerifyFactorMutation()

    const verifyMFAFactor = async (code: string) => {
      setLoading(true)
      try {
        if (factor) {
          const { data: response } = await verifyFactor({ accountId, factorId: factor.id, code })
          if (response) {
            dispatch(toSetupCompletePage(response.recoveryCodes || []))
          }
        }
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => {
      if (errors) {
        setErrorMessage(errors?.[0]?.message)
      }
    }, [errors])

    useEffect(() => {
      // @ts-ignore
      setErrorMessage(error?.data?.errors?.[0]?.message || '')
    }, [error])

    return (
      <TOTPCodeBody
        onSubmitCode={verifyMFAFactor}
        onAnotherMethod={login && hasAlternativeMethod ? () => dispatch(setupMFAForLogin()) : undefined}
        loading={loading}
        errorMessage={errorMessage}
      />
    )
  }
  Footer: React.FC = () => <></>
}

type SetupCompleteBodyProps = {
  onSubmit: () => void
  isLoading: boolean
}

const SetupCompleteBody: React.FC<SetupCompleteBodyProps> = ({ onSubmit, isLoading }) => {
  const [showToast, setShowToast] = useState(false)
  const factor = useAppSelector(selectMFAFactor)
  const recoveryCodes = useAppSelector(selectRecoveryCodes)

  const copyRecoveryCodes = async () => {
    const recoveryCodesAsText = recoveryCodes.join('\n')
    await navigator.clipboard.writeText(recoveryCodesAsText)
    setShowToast(true)
  }

  const renderRecoveryCodes = () => {
    return recoveryCodes.reduce<React.ReactNode[]>((acc, value, i, array) => {
      if (i % 2 === 0) {
        const firstCode = value
        const secondCode = array[i + 1] || ''
        acc.push(
          <Row className='d-flex flex-row gap-1 px-2' key={i}>
            <Col className='recovery-code'>{firstCode}</Col>
            <Col className='recovery-code'>{secondCode}</Col>
          </Row>,
        )
      }
      return acc
    }, [])
  }

  return (
    <Container className='d-flex flex-column'>
      <ToastContainer position='bottom-center' className='p-3 summary-toast'>
        <Toast onClose={() => setShowToast(false)} show={showToast} delay={1500} autohide>
          <Toast.Body>Copied to clipboard!</Toast.Body>
        </Toast>
      </ToastContainer>
      <Row className='pb-4'>
        <Col>
          <div>
            {factor?.type === 'sms'
              ? 'Next time you log in, you\u2019ll need to use your phone to access your account.'
              : 'Next time you log  in, you\u2019ll  need to use the authenticator app you paired to access your account.'}
          </div>
        </Col>
      </Row>
      <h6 className='py-2'>Your account uses the following method</h6>
      <Row className='completed-verification-method-box mx-0'>
        <Col xs={2} className='d-flex flex-column justify-content-center p-0'>
          <div className='verification-method-icon-box d-flex justify-content-center align-items-center'>
            <i className={`verification-method-icon bi ${factor?.type === 'sms' ? 'bi-chat-square-text' : 'bi-app-indicator'} text-white`} />
          </div>
        </Col>
        <Col className='d-flex flex-column gap-2 p-0'>
          <div className='lead-medium d-flex align-items-center'>
            {factor?.type === 'sms' ? 'TEXT MESSAGE' : 'AUTHENTICATOR APP'} <i className='verification-method-check bi bi-check-lg ps-1' />
          </div>
          <div>
            {factor?.type === 'sms' ? (
              <>
                Receive a verification code by text.
                <br />
                <strong>Your validated number: {formatPhoneNumber(factor.phoneNumber)}</strong>
              </>
            ) : (
              <>Login codes will be shown in the registered authenticator app.</>
            )}
          </div>
        </Col>
      </Row>
      {recoveryCodes.length ? (
        <>
          <h6 className='pb-2 pt-4'>Save the codes below</h6>
          <Container className='recovery-code-box'>
            <Row className='mx-0'>
              <Col xs={2} className='d-flex flex-column justify-content-center p-0'>
                <div className='recovery-code-icon-box d-flex justify-content-center align-items-center'>
                  <i className='verification-method-icon bi bi-shield-lock-fill' />
                </div>
              </Col>
              <Col className='d-flex flex-column gap-2 p-0'>
                <div className='lead-medium d-flex align-items-center'>RECOVERY CODES</div>
                <div>Use backup codes if you are unable to use your mobile device.</div>
              </Col>
            </Row>
            <Row className='pt-4 px-2'>
              <Container className='py-2 bg-white d-grid gap-1'>
                {renderRecoveryCodes()}
                <Row className='pt-2' />
                <Button variant='primaryGhost' onClick={copyRecoveryCodes}>
                  <i className='bi bi-copy' /> Copy
                </Button>
              </Container>
            </Row>
          </Container>
        </>
      ) : null}
      <Row className='mx-0 pt-5'>
        <Button variant={'primary'} className='button-tall w-100' onClick={onSubmit} loading={isLoading}>
          Done
        </Button>
      </Row>
    </Container>
  )
}

class SetupCompletePage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title='2FA setup is complete' />
  Body: React.FC<MFAModalPageBodyProps> = ({ login, onClose }) => {
    const accountId = useAppSelector(selectAccountId)
    const factor = useAppSelector(selectMFAFactor)
    const dispatch = useAppDispatch()
    const [newFactor, setNewFactor] = useState<MFAFactor | null>(null)
    const [getFactor, { isFetching }] = useLazyGetFactorQuery()
    const onDone = () => {
      if (login) {
        dispatch(toClaimCodePage(newFactor!))
      } else {
        onClose()
      }
    }

    useEffect(() => {
      if (factor) {
        getFactor({ accountId, factorId: factor.id }).then(({ data: f }) => {
          if (f) {
            setNewFactor(f)
          }
        })
      }
    }, [factor, getFactor])

    return <SetupCompleteBody onSubmit={onDone} isLoading={isFetching} />
  }
  Footer: React.FC = () => null
}

class ClaimMFAChoicePage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'2-factor authentication'} />
  Body: React.FC<MFAModalPageBodyProps> = () => {
    const accountId = useAppSelector(selectAccountId)
    const { data: factors } = useListFactorsQuery({ accountId: accountId })
    const dispatch = useAppDispatch()

    const findType = (type: MFAFactorType): MFAFactor | undefined => {
      return factors?.find((factor) => factor.type === type)
    }

    const transitionToLoginPage = (type: MFAFactorType) => {
      dispatch(toClaimCodePage(findType(type)!))
    }

    return (
      <Container className='d-flex flex-column gap-2'>
        <Row className={'pb-4'}>
          <Col>
            <div>
              As an extra measure of security 2-factor authentication is now required. From the next log in, Zocks will ask for an authentication code to verify
              you identity.
            </div>
          </Col>
        </Row>
        <h6>Choose your preferred verification method</h6>
        {findType('sms') && (
          <VerificationMethodBox
            title={'Text message'}
            description={'Receive verification codes via text message.'}
            buttonText={'Select'}
            onClick={() => transitionToLoginPage('sms')}
          />
        )}
        {findType('totp') && (
          <VerificationMethodBox
            title={'Authenticator app'}
            description={'Verify your identity by using a separate authenticator app. We recommend  Google Authenticator.'}
            buttonText={'Select'}
            onClick={() => transitionToLoginPage('totp')}
          />
        )}
        <RecoveryCodeBox onClick={() => dispatch(toClaimRecoveryCodePage())} />
      </Container>
    )
  }
  Footer: React.FC = () => <></>
}

class ClaimSMSCodePage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'2-factor authentication'} />
  Body: React.FC<MFAModalPageBodyProps> = ({ onSuccess }) => {
    const dispatch = useAppDispatch()
    const factor = useAppSelector(selectMFAFactor)! as SMSMFAFactor
    const { submitChallengeResponse, flowDisplayText, errorMessage, isLoading } = useMFAFlow()

    const submitCode = async (code: string) => {
      await submitChallengeResponse({ factorId: factor.id, code: code }, onSuccess)
    }
    return (
      <SMSCodeBody
        onSubmitCode={submitCode}
        onAnotherMethod={() => dispatch(toClaimMFAChoicePage())}
        loading={isLoading}
        errorMessage={errorMessage}
        buttonText={flowDisplayText}
      />
    )
  }
  Footer: React.FC = () => <></>
}

const TOTPCodeBody: React.FC<CodeBodyProps> = ({ onSubmitCode, onAnotherMethod, loading, errorMessage, buttonText = 'Done' }) => {
  const [code, setCode] = useState<string>('')

  return (
    <Container className='d-flex flex-column gap-4'>
      <Row className={'pb-1'}>
        <Col>
          <div>This confirms that your authenticator app is paired with your account. Make sure you use the verification code that's specific to Zocks.</div>
        </Col>
      </Row>
      <Row>
        <div className={'label-small pb-1'}>Enter the 6-digit code*</div>
        <CodeInput onChange={(e) => setCode(e)} errorMessage={errorMessage} onSubmit={() => onSubmitCode(code)} />
      </Row>
      <Row className='pb-4' />
      <Row className='mx-0'>
        <Button variant={'primary'} className='button-tall w-100' onClick={() => onSubmitCode(code)} disabled={code.length !== CODE_LENGTH} loading={loading}>
          {buttonText}
        </Button>
      </Row>
      {onAnotherMethod && (
        <Row>
          <div className='button-link-medium' role='button' onClick={onAnotherMethod}>
            Use another method
          </div>
        </Row>
      )}
      <Row />
    </Container>
  )
}

class ClaimTOTPPage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'2-factor authentication'} />
  Body: React.FC<MFAModalPageBodyProps> = ({ onSuccess }) => {
    const dispatch = useAppDispatch()
    const factor = useAppSelector(selectMFAFactor)! as SMSMFAFactor
    const { submitChallengeResponse, flowDisplayText, isLoading, errorMessage } = useMFAFlow()

    const submitCode = async (code: string) => {
      await submitChallengeResponse({ factorId: factor.id, code: code }, onSuccess)
    }
    return (
      <TOTPCodeBody
        onSubmitCode={submitCode}
        onAnotherMethod={() => dispatch(toClaimMFAChoicePage())}
        loading={isLoading}
        errorMessage={errorMessage}
        buttonText={flowDisplayText}
      />
    )
  }
  Footer: React.FC = () => <></>
}

class ClaimRecoveryCodePage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'Enter recovery code'} />
  Body: React.FC<MFAModalPageBodyProps> = ({ onSuccess }) => {
    const [recoveryCode, setRecoveryCode] = useState<string>('')
    const dispatch = useAppDispatch()
    const { submitChallengeResponse, flowDisplayText, errorMessage, isLoading } = useMFAFlow()

    const submitCode = async () => {
      await submitChallengeResponse({ recoveryCode }, onSuccess)
    }
    return (
      <Container className='d-flex flex-column gap-4'>
        <Row className={'pb-1'}>
          <Col>
            <div>If you are the account owner or your account owner can't help you in the process, please contact our support team.</div>
          </Col>
        </Row>
        <Row>
          <Col>
            <div className={'label-small pb-1'}>Enter recovery code*</div>
            <input
              className={`form-control zocks-px-1.75 zocks-py-1.25 paragraph-medium-extended text-center ${errorMessage ? 'border-danger' : ''}`}
              type='text'
              list='rooms'
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.currentTarget.value)}
              onKeyUp={async (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  await submitCode()
                }
              }}
            />
            <div className='d-flex justify-content-end paragraph-small zocks-text-danger pt-1'>{errorMessage || '\u200B'}</div>
          </Col>
        </Row>
        <Row className='pb-1' />
        <Row className='mx-0'>
          <Button variant={'primary'} className='button-tall w-100' disabled={recoveryCode.length === 0} onClick={submitCode} loading={isLoading}>
            {flowDisplayText}
          </Button>
        </Row>
        <Row>
          <div className='button-link-medium' role='button' onClick={() => dispatch(toClaimMFAChoicePage())}>
            Use another method
          </div>
        </Row>
        <Row />
      </Container>
    )
  }
  Footer: React.FC = () => <></>
}

type WarningPageProps = {
  onClick: () => void
}

const WarningPage: React.FC<WarningPageProps> = ({ onClick }) => (
  <Container className='p-0'>
    <Row className='pb-2'>
      <Col className='d-flex justify-content-center align-items-center'>
        <i className='bi bi-exclamation-triangle-fill text-danger fs-2' />
      </Col>
    </Row>
    <Row className='pt-3 pb-4'>
      <Col>
        <h4 className='text-center'>Warning</h4>
      </Col>
    </Row>
    <Row>
      <Col>
        <div className='paragraph-medium-extended'>
          2-factor authentication is mandatory on your account so you can't fully remove your authentication methods. However, you can switch to a different
          authentication method.
        </div>
      </Col>
    </Row>
    <Row className='pt-5'>
      <Col>
        <div className='d-flex w-100'>
          <Button className='w-100 button-tall' variant='primary' onClick={onClick}>
            Close
          </Button>
        </div>
      </Col>
    </Row>
  </Container>
)

type ConfirmPageProps = {
  title: string
  onCancel: () => void
  onContinue: () => void
}

const ConfirmPage: React.FC<ConfirmPageProps> = ({ title, onCancel, onContinue }) => {
  return (
    <Container className='p-0'>
      <Row className='pb-2'>
        <Col className='d-flex justify-content-center align-items-center'>
          <i className='bi bi-exclamation-triangle-fill text-warning fs-2' />
        </Col>
      </Row>
      <Row className='pt-3 pb-4 px-0'>
        <Col>
          <h4 className='text-center'>{title}</h4>
        </Col>
      </Row>
      <Row className='pb-2'>
        <Col>
          <div className='paragraph-medium-extended'>Are you sure you want to continue?</div>
        </Col>
      </Row>
      <Row className='g-3 pt-5 px-0'>
        <Col>
          <div className='d-flex w-100'>
            <Button className='w-100 button-tall' variant='secondary' onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </Col>
        <Col>
          <div className='d-flex w-100'>
            <Button className='w-100 button-tall' variant='primary' onClick={onContinue}>
              Continue
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  )
}

class CreateFactorConfirmPage implements MFAModalPage {
  Header: React.FC = () => null
  Body: React.FC<MFAModalPageBodyProps> = ({ onClose }) => {
    const dispatch = useAppDispatch()
    const factor = useAppSelector(selectMFAFactor) as SMSMFAFactor | null

    const startCreateFactor = () => {
      if (factor) {
        dispatch(toClaimCodePage(factor))
      }
    }

    return <ConfirmPage title='Before creating a new authentication method, you need to verify yourself.' onCancel={onClose} onContinue={startCreateFactor} />
  }
  Footer: React.FC = () => null
}

class DeleteFactorPage implements MFAModalPage {
  Header: React.FC = () => null
  Body: React.FC<MFAModalPageBodyProps> = ({ onClose }) => {
    const accountId = useAppSelector(selectAccountId)
    const { startDeleteFactor } = useMFAFlow()
    const { data: config } = useGetConfigQuery(accountId)
    const [listFactors] = useLazyListFactorsQuery()
    const [deleteProhibited, setDeleteProhibited] = useState<boolean | null>()

    useEffect(() => {
      if (config) {
        if (config.mfaEnforced) {
          listFactors({ accountId }).then(({ data: factors }) => {
            setDeleteProhibited(factors && factors.length == 1)
          })
        } else {
          setDeleteProhibited(false)
        }
      }
    }, [config])

    if (deleteProhibited == null) {
      return <ProgressBody />
    }

    return deleteProhibited ? (
      <WarningPage onClick={onClose} />
    ) : (
      <ConfirmPage title='Before removing this authentication method, you need to verify yourself.' onCancel={onClose} onContinue={startDeleteFactor} />
    )
  }
  Footer: React.FC = () => null
}

class UpdateSMSPage implements MFAModalPage {
  Header: React.FC = () => null
  Body: React.FC<MFAModalPageBodyProps> = ({ onClose }) => {
    const { startUpdateFactor } = useMFAFlow()

    return <ConfirmPage title='Before updating your phone number, you need to verify yourself.' onCancel={onClose} onContinue={startUpdateFactor} />
  }
  Footer: React.FC = () => null
}

class UpdateSMSPhonePage implements MFAModalPage {
  Header: React.FC = () => <MFAModalTitle title={'Update phone number'} />
  Body: React.FC<MFAModalPageBodyProps> = () => {
    const factorToUpdate = useAppSelector(selectFactorToUpdate) as SMSMFAFactor | null
    const { submitPhoneNumberUpdate, errorMessage, isLoading } = useMFAFlow()

    const validateNotSameNumber = (phoneNumber: string): string | null => {
      if (factorToUpdate && phoneNumber === factorToUpdate.phoneNumber) {
        return 'New phone number must be different from the current one'
      }
      return null
    }

    return (
      <PhoneNumberInputBody
        description='Enter your new phone number. We will send a verification code to confirm.'
        ariaLabel='Enter new phone number'
        onSubmitPhoneNumber={submitPhoneNumberUpdate}
        validate={validateNotSameNumber}
        loading={isLoading}
        flowErrorMessage={errorMessage}
      />
    )
  }
  Footer: React.FC = () => null
}

export type MFAModalProps = {
  show: boolean
  onHide: () => void
  loading: boolean
  login?: boolean
  dismissible?: boolean
  onSuccess?: () => void
}

export const MFAModal: React.FC<MFAModalProps> = ({ show, onHide, loading, login = false, dismissible = true, onSuccess }) => {
  const dispatch = useAppDispatch()
  const pageIdentifier = useAppSelector(selectPageIdentifier)

  const pages: Record<MFAModalPageIdentifier, MFAModalPage> = {
    [MFAModalPageIdentifier.LOADING]: new ProgressPage(),
    [MFAModalPageIdentifier.SETUP]: new SetupPage(),
    [MFAModalPageIdentifier.SETUP_SMS]: new SetupSMSPage(),
    [MFAModalPageIdentifier.SETUP_SMS_CODE]: new SetupSMSCodePage(),
    [MFAModalPageIdentifier.SETUP_TOTP]: new SetupTOTPPage(),
    [MFAModalPageIdentifier.SETUP_TOTP_SCAN]: new SetupTOTPScanPage(),
    [MFAModalPageIdentifier.SETUP_TOTP_CODE]: new SetupTOTPCodePage(),
    [MFAModalPageIdentifier.SETUP_COMPLETE]: new SetupCompletePage(),
    [MFAModalPageIdentifier.CLAIM_MFA_CHOICE]: new ClaimMFAChoicePage(),
    [MFAModalPageIdentifier.CLAIM_SMS_CODE]: new ClaimSMSCodePage(),
    [MFAModalPageIdentifier.CLAIM_TOTP]: new ClaimTOTPPage(),
    [MFAModalPageIdentifier.CLAIM_RECOVERY_CODE]: new ClaimRecoveryCodePage(),
    [MFAModalPageIdentifier.CREATE_FACTOR]: new CreateFactorConfirmPage(),
    [MFAModalPageIdentifier.DELETE_FACTOR]: new DeleteFactorPage(),
    [MFAModalPageIdentifier.UPDATE_SMS]: new UpdateSMSPage(),
    [MFAModalPageIdentifier.UPDATE_SMS_PHONE]: new UpdateSMSPhonePage(),
  }

  const [currentPage, setCurrentPage] = useState<MFAModalPage>(pages[MFAModalPageIdentifier.LOADING])

  useEffect(() => {
    dispatch(clearAuthErrors())
    const nextPage = pages[pageIdentifier]
    setCurrentPage(nextPage)
  }, [pageIdentifier])

  useEffect(() => {
    if (loading) {
      dispatch(resetFlow())
    }
  }, [loading])

  const { Header, Body, Footer } = currentPage
  return (
    <Modal
      contentClassName={`${login ? 'login-modal-content shadow-lg' : ''} pb-4 px-1`}
      footerClassName={'py-2'}
      show={show}
      onHide={login || !dismissible ? undefined : onHide}
      header={<></>}
      body={
        <>
          <div className='mfa-modal-header'>
            <Header />
          </div>
          <Body login={login} onClose={onHide} onSuccess={onSuccess} />
        </>
      }
      footer={<Footer />}
      centered={true}
      autoFocus={false}
      backdrop={login ? false : dismissible ? true : 'static'}
    />
  )
}
